import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PALETTES } from "@/components/palettes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TileArt = ({ project, hover = true }) => {
  const p = PALETTES[project.palette] || PALETTES.chrome;
  if (project.cover) {
    const fit = project.tile_fit || project.cover_fit;
    return (
      <img
        src={project.cover}
        alt={project.title}
        loading="lazy"
        className={`absolute inset-0 w-full h-full ${fit === "contain" ? "object-contain" : "object-cover"} transition-transform duration-700 ease-out ${hover ? "group-hover:scale-[1.03]" : ""}`}
        style={fit === "contain" ? { background: p.bg } : { background: "#171514", objectPosition: project.tile_pos || "center" }}
      />
    );
  }
  if (project.logo) {
    return (
      <span className="absolute inset-0 flex items-center justify-center p-[12%]" style={{ background: p.bg }}>
        <img
          src={project.logo}
          alt={`${project.title} logo`}
          loading="lazy"
          className={`max-w-full max-h-full object-contain transition-transform duration-700 ease-out ${hover ? "group-hover:scale-[1.04]" : ""}`}
        />
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      className={`absolute inset-0 ${p.numberChrome ? "" : ""}`}
      style={{ background: p.bg }}
    >
      <span
        className={`absolute -bottom-[8%] left-[4%] font-serif leading-none text-[42vmin] md:text-[18vmin] select-none transition-transform duration-700 ease-out ${hover ? "group-hover:-translate-y-3" : ""} ${p.numberChrome ? "text-chrome" : ""}`}
        style={p.numberChrome ? {} : { color: p.number, opacity: 0.9 }}
      >
        {project.number}
      </span>
    </span>
  );
};

const layouts = [
  "md:col-span-12 aspect-[16/9]",        // 01 ASNHVN — full-width feature
  "md:col-span-5 aspect-[3/4] md:mt-24", // 02 ANCR — tall offset
  "md:col-span-7 aspect-[4/3]",          // 03 CCDP
  "md:col-span-7 aspect-[4/3]",          // 04 NBMC
  "md:col-span-5 aspect-[3/4] md:mt-20", // 05 Drum Crown — tall, loud
  "md:col-span-4 aspect-square",         // 06 Inheira
  "md:col-span-4 aspect-[3/4]",          // 07 Stout — portrait
  "md:col-span-4 aspect-square md:mt-16",// 08 Riquina
  "md:col-span-6 aspect-[4/3]",          // 09 Kelly
  "md:col-span-6 aspect-[4/3] md:mt-20", // 10 CreateHer
  "md:col-span-5 aspect-square",         // 11 Artist Well
  "md:col-span-7 aspect-[16/9] md:mt-16",// 12 ANCRLAB — wide closer
];

const Tile = ({ project, layout, index }) => {
  const effFit = project.tile_fit || project.cover_fit;
  return (
  <Reveal delay={(index % 2) * 0.12} className={`${layout.split(" ").filter((c) => c.startsWith("md:col") || c.startsWith("md:mt")).join(" ")}`}>
    <Link
      to={`/work/${project.slug}`}
      data-testid={`project-tile-${project.slug}`}
      className="group block"
    >
      <div
        className={`relative overflow-hidden border border-charcoal/10 transition-shadow duration-700 ease-out group-hover:shadow-[0_0_90px_rgba(194,67,159,0.16),0_0_28px_rgba(122,79,208,0.11)] ${layout.split(" ").find((c) => c.startsWith("aspect")) || "aspect-[4/3]"}`}
        style={{ background: project.cover && effFit !== "contain" ? "#171514" : undefined }}
      >
        <TileArt project={project} />
        <span
          aria-hidden="true"
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
          style={{
            background:
              "linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.35) 48%, rgba(179,163,212,0.18) 55%, transparent 70%)",
          }}
        />
      </div>
      <div className="flex items-baseline justify-between mt-5 border-t border-charcoal/10 pt-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-charcoal-mute mb-1">
            No. {project.number} · {project.tags[0]} · {project.year}
          </p>
          <h3 className="font-serif text-xl md:text-2xl group-hover:italic transition-all duration-300">
            {project.title}
          </h3>
        </div>
        <ArrowUpRight
          className="w-5 h-5 shrink-0 text-charcoal-mute group-hover:text-charcoal group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300"
          strokeWidth={1.25}
        />
      </div>
    </Link>
  </Reveal>
  );
};

const KitCard = ({ project, index }) => (
  <Reveal delay={(index % 3) * 0.08}>
    <Link
      to={`/work/${project.slug}`}
      data-testid={`kit-card-${project.slug}`}
      className="group block"
    >
      <div className="relative overflow-hidden border border-charcoal/10 aspect-[4/3] transition-shadow duration-700 ease-out group-hover:shadow-[0_0_90px_rgba(194,67,159,0.16),0_0_28px_rgba(122,79,208,0.11)]">
        <TileArt project={project} />
      </div>
      <div className="flex items-baseline justify-between mt-4 border-t border-charcoal/10 pt-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-charcoal-mute mb-1">
            Brand Kit Presentation · {project.year}
          </p>
          <h4 className="font-serif text-lg md:text-xl group-hover:italic transition-all duration-300">
            {project.title}
          </h4>
        </div>
        <ArrowUpRight
          className="w-4 h-4 shrink-0 text-charcoal-mute group-hover:text-charcoal group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300"
          strokeWidth={1.25}
        />
      </div>
    </Link>
  </Reveal>
);

export const SelectedWork = () => {
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/projects`)
      .then((res) => setProjects((res.data.projects || []).filter((p) => !p.archive)))
      .catch(() => setError(true));
  }, []);

  const main = projects.filter((p) => !p.brandkit);
  const brandKits = projects.filter((p) => p.brandkit);
  // Brand kit presentations may live in the archive — fetch them separately
  const [archiveKits, setArchiveKits] = useState([]);
  useEffect(() => {
    axios
      .get(`${API}/projects`)
      .then((res) => setArchiveKits((res.data.projects || []).filter((p) => p.archive && p.brandkit)))
      .catch(() => {});
  }, []);
  const kitCards = [...brandKits, ...archiveKits];

  return (
    <section id="work" data-testid="selected-work-section" className="px-6 md:px-12 lg:px-24 py-28 md:py-44">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-24">
          <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight">
            Selected
            <br />
            <span className="italic text-chrome">Work</span>
          </h2>
          <p className="max-w-xs text-sm text-charcoal-soft leading-relaxed md:text-right">
            Brand worlds — architecture, technology, culture, education, beauty,
            music, and beyond. The full archive lives in All Work.
          </p>
        </div>
      </Reveal>

      {error && (
        <p data-testid="work-error-message" className="text-sm text-charcoal-mute">
          The work could not be loaded. Please refresh.
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-12 gap-x-8 gap-y-16 md:gap-y-12">
        {main.map((project, i) => (
          <Tile key={project.slug} project={project} layout={project.tile_layout || layouts[i % layouts.length]} index={i} />
        ))}
      </div>

      {kitCards.length > 0 && (
        <div data-testid="brand-kit-section" className="mt-24 md:mt-32">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-3 mb-10 md:mb-12 border-t border-charcoal/10 pt-8">
              <h3 className="font-serif italic text-3xl md:text-5xl tracking-tight">
                Brand Kit <span className="text-chrome">Presentations</span>
              </h3>
              <p className="text-[10px] uppercase tracking-[0.28em] text-charcoal-mute">
                One client. One presentation. Delivered complete.
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-12">
            {[...kitCards]
              .sort((a, b) => parseInt(b.number, 10) - parseInt(a.number, 10))
              .map((project, i) => (
                <KitCard key={project.slug} project={project} index={i} />
              ))}
          </div>
        </div>
      )}

      <Reveal className="mt-20 md:mt-28 flex justify-end">
        <Link
          to="/work"
          data-testid="view-all-work-link"
          className="group inline-flex items-baseline gap-4"
        >
          <span className="font-serif italic text-3xl md:text-5xl group-hover:text-chrome transition-colors duration-300">
            View All Work
          </span>
          <ArrowRight className="w-6 h-6 self-center text-charcoal-mute group-hover:translate-x-1 transition-transform duration-300" strokeWidth={1.25} />
        </Link>
      </Reveal>
    </section>
  );
};
